'use client';

import { useState } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  IconButton,
  Typography} from '@mui/material';

function getYouTubeId(url) {
  const regex =
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
}

export default function YouTubeEmbed({
  src,
  title = 'YouTube video',
  autoplay = false,
  controls = true,
}) {
  const [hasError, setHasError] = useState(false);
  const youtubeId = getYouTubeId(src);
  const watchUrl = `https://www.youtube.com/watch?v=${youtubeId}`;

  if (!youtubeId) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        <AlertTitle>URL de YouTube inválida</AlertTitle>
        Verifica que la URL esté bien escrita.
      </Alert>
    );
  }

  const params = new URLSearchParams();
  if (autoplay) params.append('autoplay', '1');
  if (!controls) params.append('controls', '0');
  params.append('modestbranding', '1');
  params.append('rel', '0');

  const embedUrl = `https://www.youtube.com/embed/${youtubeId}?${params.toString()}`;

  if (hasError) {
    return (
      <Box
        sx={{
          bgcolor: 'grey.900',
          color: 'white',
          borderRadius: 2,
          overflow: 'hidden',
          aspectRatio: '16/9',
          p: 4,
          textAlign: 'center',
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 48, color: 'error.light', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          Video no disponible
        </Typography>
        <Typography variant="body2" color="grey.400" sx={{ mb: 2 }}>
          Este video no puede mostrarse aquí debido a restricciones del propietario.
        </Typography>
        <Button
          variant="contained"
          color="error"
          startIcon={<OpenInNewIcon />}
          onClick={() => window.open(watchUrl, '_blank')}
        >
          Ver en YouTube
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'black',
        borderRadius: 2,
        overflow: 'hidden',
        position: 'relative',
        aspectRatio: '16/9',
        width: '100%',
        maxWidth: 960,
        mx: 'auto',
      }}
    >
      <iframe
        src={embedUrl}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
        className="youtube-iframe"
        onError={() => setHasError(true)}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
        }}
      />

      {/* Etiqueta YouTube */}
      <Chip
        label="YouTube"
        icon={<PlayArrowIcon />}
        size="small"
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          bgcolor: 'error.main',
          color: 'white',
          fontWeight: 'bold',
        }}
      />

      {/* Botón externo */}
      <IconButton
        onClick={() => window.open(watchUrl, '_blank')}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          bgcolor: 'rgba(0,0,0,0.6)',
          color: 'white',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
        }}
        size="small"
      >
        <OpenInNewIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
